/**
 * Needs Assessment Classification Engine
 *
 * Evaluates questionnaire responses across five dimensions and produces
 * actionable tags + recommended next steps for the Hey Freeda team.
 *
 * All classification is based on answer codes (language-independent).
 * Pure functions, no side effects — safe to call from any context.
 */

import type {QuestionnaireValue} from '@refinio/one.models/lib/models/QuestionnaireModel.js';

// ─── Types ───────────────────────────────────────────────────────────

export type Level = 'kritisch' | 'eingeschränkt' | 'stabil' | 'nicht-beurteilbar';
export type ReadinessLevel = 'bereit' | 'in-vorbereitung' | 'nicht-fokussiert' | 'nicht-beurteilbar';
export type EignungLevel = 'hoch' | 'mittel' | 'gering' | 'nicht-beurteilbar';
export type SupportLevel = 'hoch' | 'mittel' | 'gering' | 'nicht-beurteilbar';

export interface NeedsProfile {
    /** Basic needs & housing stability */
    stabilitaet: Level;
    /** Education status & barriers */
    bildung: Level;
    /** Eligibility for German language promotion */
    deutschfoerderung: EignungLevel;
    /** Readiness for pathway to Germany */
    deutschlandReadiness: ReadinessLevel;
    /** Level of personal support needed */
    unterstuetzungsbedarf: SupportLevel;
    /** Automatic flags for quick triage */
    tags: Tag[];
    /** Recommended next steps (ordered by priority) */
    empfehlungen: string[];
}

export type Tag =
    | 'grundversorgung-zuerst'
    | 'deutschfoerderung-kandidatin'
    | 'sofortkontakt'
    | 'orientierung-noetig'
    | 'familiaerer-widerstand'
    | 'betreuungspflichten'
    | 'kein-internet'
    | 'bildung-gefaehrdet'
    | 'belastung';

const TAG_LABELS: Record<Tag, string> = {
    'grundversorgung-zuerst': 'Grundversorgung zuerst',
    'deutschfoerderung-kandidatin': 'Deutschförderung-Kandidatin',
    'sofortkontakt': 'Sofortkontakt empfohlen',
    'orientierung-noetig': 'Orientierung nötig',
    'familiaerer-widerstand': 'Familiärer Widerstand',
    'betreuungspflichten': 'Betreuungspflichten',
    'kein-internet': 'Kein stabiles Internet',
    'bildung-gefaehrdet': 'Bildung gefährdet',
    'belastung': 'Persönliche Belastung'
};

const TAG_ICONS: Record<Tag, string> = {
    'grundversorgung-zuerst': '\u{1F534}',
    'deutschfoerderung-kandidatin': '\u{1F7E2}',
    'sofortkontakt': '\u{1F6A8}',
    'orientierung-noetig': '\u{1F7E1}',
    'familiaerer-widerstand': '\u{26A0}\uFE0F',
    'betreuungspflichten': '\u{1F476}',
    'kein-internet': '\u{1F4F5}',
    'bildung-gefaehrdet': '\u{1F4D9}',
    'belastung': '\u{1F49C}'
};

// ─── Helpers ─────────────────────────────────────────────────────────

function code(answers: Map<string, QuestionnaireValue[]>, linkId: string): string | undefined {
    return answers.get(linkId)?.[0]?.valueCoding?.code;
}

function codes(answers: Map<string, QuestionnaireValue[]>, linkId: string): string[] {
    return (answers.get(linkId) ?? [])
        .map(v => v.valueCoding?.code)
        .filter((c): c is string => c !== undefined);
}

function has(answers: Map<string, QuestionnaireValue[]>, linkId: string): boolean {
    const a = answers.get(linkId);
    return a !== undefined && a.length > 0;
}

// ─── Dimension: Stabilität ───────────────────────────────────────────

function classifyStabilitaet(answers: Map<string, QuestionnaireValue[]>): Level {
    const housing = code(answers, 'housingSafe');
    const medical = code(answers, 'medicalAccess');
    const missing = codes(answers, 'missingItems');
    const needsFirst = code(answers, 'needsResolutionFirst');

    // Not enough data
    if (!housing && !medical && missing.length === 0) return 'nicht-beurteilbar';

    // Kritisch: unsafe housing, urgent unresolved needs, or lacking shelter/food
    const criticalMissing = missing.some(c => c === '1' || c === '2'); // shelter, food
    if (housing === '4' || needsFirst === 'yes' || criticalMissing) return 'kritisch';

    // Eingeschränkt
    const moderateMissing = missing.filter(c => c !== 'none' && c !== 'other').length >= 2;
    if (housing === '3' || medical === '3' || needsFirst === 'maybe' || moderateMissing) return 'eingeschränkt';

    // Stabil
    if ((housing === '1' || housing === '2') && (medical === '1' || medical === '2')) return 'stabil';

    return 'eingeschränkt';
}

// ─── Dimension: Bildung ──────────────────────────────────────────────

function classifyBildung(answers: Map<string, QuestionnaireValue[]>): Level {
    const studying = code(answers, 'currentlyStudying');
    const concern = code(answers, 'educationConcern');
    const barriers = codes(answers, 'educationBarriers').filter(c => c !== 'none' && c !== 'other');

    if (!studying) return 'nicht-beurteilbar';

    if (concern === 'yes' && barriers.length >= 2) return 'kritisch';
    if (concern === 'yes' || (studying === 'want-to' && barriers.length > 0)) return 'eingeschränkt';
    if (studying === 'no') return 'eingeschränkt';
    if (studying === 'yes' && concern === 'no') return 'stabil';
    if (studying === 'want-to') return 'eingeschränkt';

    return 'eingeschränkt';
}

// ─── Dimension: Deutschförderung-Eignung ─────────────────────────────

function classifyDeutschfoerderung(answers: Map<string, QuestionnaireValue[]>): EignungLevel {
    const wantsLang = code(answers, 'wantsLanguage');
    const whichLang = code(answers, 'whichLanguage');
    const direction = code(answers, 'generalDirection');
    const smartphone = code(answers, 'hasSmartphone');
    const internet = code(answers, 'internetAccess');
    const pathGermany = codes(answers, 'pathToGermany');

    // Not interested in languages at all
    if (wantsLang === 'no') return 'gering';
    if (!wantsLang) return 'nicht-beurteilbar';

    const wantsGerman = whichLang === 'german' || whichLang === 'both';
    const directionGermany = direction === 'germany' || direction === 'both';
    const hasDevice = smartphone === '1' || smartphone === '2';
    const hasInternet = internet === '1' || internet === '2';
    const pathIncludesGermany = pathGermany.some(c => c === '1' || c === '2' || c === '3' || c === '4');

    if (wantsGerman && directionGermany && hasDevice) return 'hoch';
    if (wantsGerman && (directionGermany || pathIncludesGermany)) return 'mittel';
    if (wantsGerman) return 'mittel';

    return 'gering';
}

// ─── Dimension: Deutschland-Readiness ────────────────────────────────

function classifyDeutschlandReadiness(answers: Map<string, QuestionnaireValue[]>): ReadinessLevel {
    const direction = code(answers, 'generalDirection');
    const germanLevel = code(answers, 'germanLevel');
    const pathGermany = codes(answers, 'pathToGermany');
    const fieldInterest = codes(answers, 'fieldOfInterest');

    if (!direction) return 'nicht-beurteilbar';

    if (direction === 'nepal') return 'nicht-fokussiert';

    const hasGerman = germanLevel && germanLevel !== '1' && germanLevel !== '7'; // not "keine Kenntnisse" or "weiß nicht"
    const hasClearPath = pathGermany.length > 0 && !pathGermany.includes('5'); // not only "unsicher"
    const hasFieldInterest = fieldInterest.length > 0 && !fieldInterest.every(c => c === '6'); // not only "unsicher"

    if (hasGerman && hasClearPath && hasFieldInterest) return 'bereit';
    if (direction === 'germany' || direction === 'both') return 'in-vorbereitung';

    return 'nicht-fokussiert';
}

// ─── Dimension: Unterstützungsbedarf ─────────────────────────────────

function classifySupportNeed(answers: Map<string, QuestionnaireValue[]>): SupportLevel {
    const burden = code(answers, 'personalBurden');
    const familySupport = code(answers, 'familySupport');
    const dependents = code(answers, 'dependents');

    if (!burden && !familySupport) return 'nicht-beurteilbar';

    const highBurden = burden === 'yes';
    const familyAgainst = familySupport === '3';
    const hasDependents = dependents && dependents !== 'no' && dependents !== 'prefer-not';

    if (highBurden && (familyAgainst || hasDependents)) return 'hoch';
    if (highBurden || familyAgainst) return 'hoch';
    if (burden === 'maybe' || familySupport === '2' || familySupport === '5' || hasDependents) return 'mittel';

    return 'gering';
}

// ─── Tags ────────────────────────────────────────────────────────────

function deriveTags(
    answers: Map<string, QuestionnaireValue[]>,
    stabilitaet: Level,
    bildung: Level,
    deutschfoerderung: EignungLevel,
    unterstuetzungsbedarf: SupportLevel
): Tag[] {
    const tags: Tag[] = [];

    if (stabilitaet === 'kritisch') tags.push('grundversorgung-zuerst');

    if (deutschfoerderung === 'hoch') tags.push('deutschfoerderung-kandidatin');

    const contactSafe = code(answers, 'contactSafe');
    if (contactSafe === 'yes' && (stabilitaet === 'kritisch' || code(answers, 'personalBurden') === 'yes')) {
        tags.push('sofortkontakt');
    }

    const futureOutlook = code(answers, 'futureOutlook');
    const pathGermany = codes(answers, 'pathToGermany');
    if (futureOutlook === '4' || pathGermany.includes('5')) {
        tags.push('orientierung-noetig');
    }

    if (code(answers, 'familySupport') === '3') tags.push('familiaerer-widerstand');

    const dependents = code(answers, 'dependents');
    if (dependents && dependents !== 'no' && dependents !== 'prefer-not') tags.push('betreuungspflichten');

    const internet = code(answers, 'internetAccess');
    if (internet === '3' || internet === '4' || code(answers, 'hasSmartphone') === '3') {
        tags.push('kein-internet');
    }

    if (bildung === 'kritisch') tags.push('bildung-gefaehrdet');

    if (code(answers, 'personalBurden') === 'yes') tags.push('belastung');

    return tags;
}

// ─── Empfehlungen ────────────────────────────────────────────────────

function deriveEmpfehlungen(
    answers: Map<string, QuestionnaireValue[]>,
    profile: Omit<NeedsProfile, 'empfehlungen'>
): string[] {
    const empfehlungen: string[] = [];

    // Priority 1: Critical stability
    if (profile.stabilitaet === 'kritisch') {
        empfehlungen.push('Grundversorgung und Sicherheit klären (Unterkunft, Nahrung, medizinische Versorgung)');
        const urgent = answers.get('urgentIssue')?.[0]?.valueString;
        if (urgent) {
            empfehlungen.push(`Dringendes Anliegen ansprechen: "${urgent}"`);
        }
    }

    // Priority 2: Sofortkontakt
    if (profile.tags.includes('sofortkontakt')) {
        const method = code(answers, 'contactMethod');
        const phone = answers.get('contactPhone')?.[0]?.valueString;
        const email = answers.get('contactEmail')?.[0]?.valueString;
        const contact = phone || email || method || 'Kontaktdaten prüfen';
        empfehlungen.push(`Zeitnah Kontakt aufnehmen (${contact})`);
    }

    // Priority 3: Personal burden
    if (profile.tags.includes('belastung')) {
        empfehlungen.push('Persönliches Gespräch anbieten — Belastung wurde signalisiert');
    }

    // Priority 4: Family resistance
    if (profile.tags.includes('familiaerer-widerstand')) {
        empfehlungen.push('Familiäre Situation sensibel klären — Familie ist gegen Bildungs-/Zukunftspläne');
    }

    // Priority 5: Education at risk
    if (profile.tags.includes('bildung-gefaehrdet')) {
        const barriers = codes(answers, 'educationBarriers')
            .filter(c => c !== 'none' && c !== 'other');
        if (barriers.length > 0) {
            empfehlungen.push('Bildungsbarrieren adressieren (Kosten, Transport, Materialien etc.)');
        }
    }

    // Priority 6: German promotion
    if (profile.deutschfoerderung === 'hoch') {
        const level = code(answers, 'germanLevel');
        const levelText = level === '1' ? 'ab Null' : level === '2' ? 'Anfängerin' : `Niveau ${level}`;
        empfehlungen.push(`Für Deutschförderung vormerken (aktuelles Niveau: ${levelText})`);
    } else if (profile.deutschfoerderung === 'mittel') {
        empfehlungen.push('Deutschförderung prüfen — Interesse vorhanden, Rahmenbedingungen klären');
    }

    // Priority 7: Germany readiness
    if (profile.deutschlandReadiness === 'bereit') {
        empfehlungen.push('Konkreten Deutschland-Weg besprechen (Ausbildung, Studium, Visum)');
    } else if (profile.deutschlandReadiness === 'in-vorbereitung') {
        empfehlungen.push('Orientierungsgespräch zu Möglichkeiten in Deutschland anbieten');
    }

    // Priority 8: No internet
    if (profile.tags.includes('kein-internet')) {
        empfehlungen.push('Offline-Lernmöglichkeiten oder Gerätezugang prüfen');
    }

    // Priority 9: Caregiving
    if (profile.tags.includes('betreuungspflichten')) {
        empfehlungen.push('Betreuungspflichten bei Planung berücksichtigen (zeitliche Verfügbarkeit)');
    }

    // Priority 10: Orientation
    if (profile.tags.includes('orientierung-noetig') && empfehlungen.length < 3) {
        empfehlungen.push('Allgemeines Orientierungsgespräch — Teilnehmerin sieht wenig Perspektiven');
    }

    // Fallback
    if (empfehlungen.length === 0) {
        empfehlungen.push('Reguläre Kontaktaufnahme — keine dringenden Handlungsbedarfe erkannt');
    }

    return empfehlungen;
}

// ─── Main ────────────────────────────────────────────────────────────

export function classifyNeeds(answers: Map<string, QuestionnaireValue[]>): NeedsProfile {
    const stabilitaet = classifyStabilitaet(answers);
    const bildung = classifyBildung(answers);
    const deutschfoerderung = classifyDeutschfoerderung(answers);
    const deutschlandReadiness = classifyDeutschlandReadiness(answers);
    const unterstuetzungsbedarf = classifySupportNeed(answers);

    const tags = deriveTags(answers, stabilitaet, bildung, deutschfoerderung, unterstuetzungsbedarf);

    const partial = {stabilitaet, bildung, deutschfoerderung, deutschlandReadiness, unterstuetzungsbedarf, tags};
    const empfehlungen = deriveEmpfehlungen(answers, partial);

    return {...partial, empfehlungen};
}

// ─── Formatting ──────────────────────────────────────────────────────

const LEVEL_ICONS: Record<string, string> = {
    'kritisch': '\u{1F534}',
    'eingeschränkt': '\u{1F7E1}',
    'stabil': '\u{1F7E2}',
    'hoch': '\u{1F534}',
    'mittel': '\u{1F7E1}',
    'gering': '\u{1F7E2}',
    'bereit': '\u{1F7E2}',
    'in-vorbereitung': '\u{1F7E1}',
    'nicht-fokussiert': '\u26AA',
    'nicht-beurteilbar': '\u2796'
};

function capitalize(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ');
}

export function formatProfileSummary(
    profile: NeedsProfile,
    answers: Map<string, QuestionnaireValue[]>
): string {
    const name = answers.get('participantName')?.[0]?.valueString ?? 'Anonym';
    const age = answers.get('age')?.[0]?.valueCoding?.display ?? '\u2014';
    const location = answers.get('location')?.[0]?.valueString ?? '\u2014';
    const phone = answers.get('contactPhone')?.[0]?.valueString;
    const email = answers.get('contactEmail')?.[0]?.valueString;
    const contactPerson = answers.get('contactPerson')?.[0]?.valueString;
    const contactMethod = answers.get('contactMethod')?.[0]?.valueCoding?.display;
    const referral = answers.get('referralSource')?.[0]?.valueCoding?.display ?? '\u2014';
    const date = new Date().toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'});

    // Build identifier: Name + Phone/Email
    const identParts: string[] = [];
    if (name !== 'Anonym') identParts.push(name);
    if (phone) identParts.push(phone);
    if (email) identParts.push(email);
    const identifier = identParts.length > 0 ? identParts.join(' | ') : 'Anonym';

    const lines: string[] = [
        '\u2550'.repeat(50),
        `TEILNEHMERIN ${identifier}`,
        '\u2550'.repeat(50),
        '',
    ];

    // Header block
    lines.push(`Name:           ${name}`);
    lines.push(`Alter:          ${age}`);
    lines.push(`Ort:            ${location}`);
    if (phone) lines.push(`Telefon:        ${phone}${contactMethod ? ` (${contactMethod})` : ''}`);
    if (email) lines.push(`E-Mail:         ${email}`);
    if (contactPerson) lines.push(`Kontaktperson:  ${contactPerson}`);
    lines.push(`Quelle:         ${referral}`);
    lines.push(`Datum:          ${date}`);
    lines.push('');

    // Tags
    if (profile.tags.length > 0) {
        const tagStr = profile.tags
            .map(t => `${TAG_ICONS[t]} ${TAG_LABELS[t]}`)
            .join('  |  ');
        lines.push(`TAGS:  ${tagStr}`);
        lines.push('');
    }

    // Dimensions
    lines.push('AUSWERTUNG');
    lines.push('\u2500'.repeat(50));

    const dims: [string, string][] = [
        ['Stabilit\u00e4t', profile.stabilitaet],
        ['Bildung', profile.bildung],
        ['Deutschf\u00f6rderung', profile.deutschfoerderung],
        ['Deutschland-Readiness', profile.deutschlandReadiness],
        ['Unterst\u00fctzungsbedarf', profile.unterstuetzungsbedarf]
    ];

    for (const [label, level] of dims) {
        const icon = LEVEL_ICONS[level] ?? '\u2796';
        lines.push(`${icon} ${label.padEnd(24)} ${capitalize(level)}`);
    }

    lines.push('\u2500'.repeat(50));
    lines.push('');

    // Recommendations
    lines.push('N\u00c4CHSTE SCHRITTE');
    profile.empfehlungen.forEach((e, i) => {
        lines.push(`${i + 1}. ${e}`);
    });
    lines.push('');

    // Situation summary
    lines.push('SITUATION');
    const situation = answers.get('situation')?.[0]?.valueCoding?.display;
    const living = answers.get('livingSituation')?.[0]?.valueCoding?.display;
    if (situation) lines.push(`- ${situation}`);
    if (living) lines.push(`- Lebt: ${living}`);

    const dependents = answers.get('dependents')?.[0]?.valueCoding?.display;
    if (dependents && code(answers, 'dependents') !== 'no') lines.push(`- Betreuung: ${dependents}`);

    const familySup = answers.get('familySupport')?.[0]?.valueCoding?.display;
    if (familySup) lines.push(`- Familie: ${familySup}`);

    const germanLvl = answers.get('germanLevel')?.[0]?.valueCoding?.display;
    const englishLvl = answers.get('englishLevel')?.[0]?.valueCoding?.display;
    if (germanLvl) lines.push(`- Deutsch: ${germanLvl}`);
    if (englishLvl) lines.push(`- Englisch: ${englishLvl}`);

    const smartphone = answers.get('hasSmartphone')?.[0]?.valueCoding?.display;
    const internet = answers.get('internetAccess')?.[0]?.valueCoding?.display;
    if (smartphone) lines.push(`- Smartphone: ${smartphone}${internet ? `, Internet: ${internet}` : ''}`);

    const direction = answers.get('generalDirection')?.[0]?.valueCoding?.display;
    if (direction) lines.push(`- Richtung: ${direction}`);

    const pathItems = (answers.get('pathToGermany') ?? []).map(v => v.valueCoding?.display).filter(Boolean);
    if (pathItems.length > 0) lines.push(`- Weg: ${pathItems.join(', ')}`);

    const fieldItems = (answers.get('fieldOfInterest') ?? []).map(v => v.valueCoding?.display).filter(Boolean);
    if (fieldItems.length > 0) lines.push(`- Bereich: ${fieldItems.join(', ')}`);

    const supportItems = (answers.get('helpfulSupport') ?? []).map(v => v.valueCoding?.display).filter(Boolean);
    if (supportItems.length > 0) lines.push(`- W\u00fcnscht sich: ${supportItems.join(', ')}`);

    lines.push('');
    lines.push('\u2550'.repeat(50));

    return lines.join('\n');
}

export {TAG_LABELS, TAG_ICONS};
